import { useState } from 'react';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  text: string;
  rating: number;
}

interface TestimonialsTabProps {
  data: Testimonial[];
  add: (item: Omit<Testimonial, 'id'> & { id?: number | string }) => Promise<void>;
  update: (id: number, updates: Partial<Testimonial>) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

export default function TestimonialsTab({
  data,
  add,
  update,
  remove,
}: TestimonialsTabProps) {
  const [editing, setEditing] = useState<Testimonial | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-brand-black text-xl">Testimonios</h3>
        <button
          onClick={() => {
            setIsAdding(true);
            setEditing(null);
          }}
          className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
        >
          + Nuevo testimonio
        </button>
      </div>

      {(isAdding || editing) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const payload = {
              name: fd.get('name') as string,
              location: fd.get('location') as string,
              text: fd.get('text') as string,
              rating: Number(fd.get('rating')),
            };
            if (editing) {
              update(editing.id, payload);
              setEditing(null);
            } else {
              add(payload);
              setIsAdding(false);
            }
          }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h4 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Testimonio' : 'Nuevo Testimonio'}
            </h4>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
              }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Nombre
              </label>
              <input
                name="name"
                defaultValue={editing?.name ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Ubicación
              </label>
              <input
                name="location"
                defaultValue={editing?.location ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Texto
            </label>
            <textarea
              name="text"
              defaultValue={editing?.text ?? ''}
              rows={4}
              required
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
            />
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Valoración (1-5)
            </label>
            <input
              name="rating"
              type="number"
              min={1}
              max={5}
              defaultValue={editing?.rating ?? 5}
              required
              className="mt-1 w-full sm:w-32 border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
              }}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.map((t) => (
          <div
            key={t.id}
            className="bg-white border border-brand-gray-200 rounded-2xl p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex gap-0.5">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <i
                    key={i}
                    className="ri-star-fill text-brand-sage text-xs"
                  />
                ))}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    setEditing(t);
                    setIsAdding(false);
                  }}
                  className="w-7 h-7 rounded-md bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                >
                  <i className="ri-pencil-line text-xs" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`¿Eliminar testimonio de ${t.name}?`))
                      remove(t.id);
                  }}
                  className="w-7 h-7 rounded-md bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                >
                  <i className="ri-delete-bin-line text-xs" />
                </button>
              </div>
            </div>
            <p className="font-sans text-brand-gray-600 text-sm leading-relaxed mb-4">
              &ldquo;{t.text}&rdquo;
            </p>
            <div>
              <p className="font-sans text-brand-black text-sm font-medium">
                {t.name}
              </p>
              <p className="font-sans text-brand-gray-400 text-xs">
                {t.location}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}